import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { attemptApi, type SubmitResult } from "@/lib/attemptApi";
import { clearSession } from "@/lib/session";
import { BrandHeader } from "@/components/BrandHeader";

export default function Result() {
  const { attemptId = "" } = useParams();
  const [params] = useSearchParams();
  const auto = params.get("auto") === "1";
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The submit endpoint is idempotent — safe to call again to fetch the result.
    (async () => {
      try {
        const r = await attemptApi.submit(attemptId);
        setResult(r);
      } catch (e: any) {
        setError(e?.response?.data?.detail ?? "Could not load result.");
      } finally {
        // Result screen is a terminal state — clear the session either way.
        setTimeout(clearSession, 5_000);
      }
    })();
  }, [attemptId]);

  if (error) return <Centered>{error}</Centered>;
  if (!result) return <Centered>Loading…</Centered>;

  const r = result.result;
  const hasCustomMessage = !!result.completionMessage?.trim();

  return (
    <>
      <BrandHeader />
      <main className="mx-auto max-w-xl p-6">
      <div className="card p-8 text-center">
        <div className="text-sm text-brand-ink/60">
          {auto ? "Auto-submitted (time up)" : "Submitted"}
        </div>
        <h1 className="mt-2 text-3xl font-bold text-brand">Thank you!</h1>

        {hasCustomMessage && (
          <div
            className="prose prose-sm mx-auto mt-6 max-w-none text-brand-ink"
            dangerouslySetInnerHTML={{ __html: result.completionMessage }}
          />
        )}

        {r ? (
          <>
            <div className="mt-6 text-5xl font-bold text-brand-ink">{r.finalScore}</div>
            {r.passFail && (
              <div className={`mt-2 text-sm font-semibold ${r.passFail === "Pass" ? "text-emerald-600" : "text-brand"}`}>
                {r.passFail}
              </div>
            )}

            <dl className="mt-6 grid grid-cols-3 gap-4 text-sm">
              <Stat label="Correct" value={r.correct} />
              <Stat label="Wrong" value={r.wrong} />
              <Stat label="Skipped" value={r.skipped} />
              <Stat label="Positive" value={r.positiveTotal} />
              <Stat label="Negative" value={r.negativeTotal} />
              <Stat label="Penalty" value={r.penaltyTotal} />
            </dl>
          </>
        ) : !hasCustomMessage && (
          <p className="mt-6 text-sm text-brand-ink/70">
            Your response has been recorded. Results will be released later per this quiz's policy.
          </p>
        )}
      </div>
      </main>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-brand-light p-3">
      <div className="text-xs uppercase tracking-wide text-brand-ink/60">{label}</div>
      <div className="mt-1 text-lg font-semibold text-brand-ink">{value}</div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}
